import React, { useState, useEffect } from 'react';
import { useAppDispatch } from '../store/hooks';
import { addToast } from '../store/slices/toastsSlice';
import { inviteApiService } from '../services/inviteApiService';
import { Invite } from '../types/invite';
import { UserRole } from '../types/auth';

interface InviteFormData {
  email: string;
  role: UserRole;
}

export const ManageTeam: React.FC = () => {
  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState<InviteFormData>({ email: '', role: 'admin' });
  const [validationError, setValidationError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loadingInvites, setLoadingInvites] = useState(true);

  // Fetch invites on mount
  useEffect(() => {
    fetchInvites();
  }, []);

  const fetchInvites = async () => {
    try {
      setLoadingInvites(true);
      const data = await inviteApiService.fetchInvites();
      setInvites(data);
    } catch (error: any) {
      dispatch(addToast(error.message || 'Failed to load invitations', 'error'));
    } finally {
      setLoadingInvites(false);
    }
  };

  const validateEmail = (email: string): string | null => {
    if (!email.trim()) {
      return 'Email is required';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Invalid email format';
    }
    return null;
  };

  const handleEmailChange = (email: string) => {
    setFormData(prev => ({ ...prev, email }));
    if (validationError) {
      setValidationError('');
    }
  };

  const handleRoleChange = (role: UserRole) => {
    setFormData(prev => ({ ...prev, role }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const error = validateEmail(formData.email);
    if (error) {
      setValidationError(error);
      return;
    }

    setLoading(true);

    try {
      await inviteApiService.sendInvite({ email: formData.email, role: formData.role });
      dispatch(addToast(`Invitation sent to ${formData.email}`, 'success'));
      setFormData({ email: '', role: 'admin' });
      setValidationError('');

      // Refresh invites list
      await fetchInvites();
    } catch (error: any) {
      dispatch(addToast(error.message || 'Failed to send invitation', 'error'));
      setValidationError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getInviteStatus = (invite: Invite): { text: string; color: string } => {
    if (invite.usedAt) {
      return { text: 'Accepted', color: '#4caf50' };
    }

    const now = new Date();
    const expiresAt = new Date(invite.expiresAt);

    if (now > expiresAt) {
      return { text: 'Expired', color: '#f44336' };
    }

    return { text: 'Pending', color: '#ff9800' };
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.heading}>Manage Team</h1>
        <p style={styles.description}>
          Invite team members to access Deb's Meatbuns Manager
        </p>
      </div>

      {/* Invite Form */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Send Invitation</h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formRow}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={e => handleEmailChange(e.target.value)}
                style={{
                  ...styles.input,
                  ...(validationError ? styles.inputError : {})
                }}
                placeholder="colleague@example.com"
                disabled={loading}
              />
              {validationError && (
                <div style={styles.error}>{validationError}</div>
              )}
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Role</label>
              <select
                value={formData.role}
                onChange={e => handleRoleChange(e.target.value as UserRole)}
                style={styles.select}
                disabled={loading}
              >
                <option value="admin">Admin - Full access</option>
                <option value="team">Team - Workflow only</option>
              </select>
            </div>
            <button
              type="submit"
              style={{
                ...styles.button,
                ...(loading ? styles.buttonDisabled : {})
              }}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>

      {/* Invitations List */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Sent Invitations</h2>

        {loadingInvites ? (
          <p style={styles.loadingText}>Loading invitations...</p>
        ) : invites.length === 0 ? (
          <p style={styles.emptyText}>No invitations sent yet</p>
        ) : (
          <div style={styles.table}>
            <div style={styles.tableHeader}>
              <div style={{ ...styles.tableCell, flex: 2 }}>Email</div>
              <div style={{ ...styles.tableCell, flex: 1 }}>Role</div>
              <div style={{ ...styles.tableCell, flex: 1.5 }}>Invited By</div>
              <div style={styles.tableCell}>Status</div>
              <div style={{ ...styles.tableCell, flex: 1.5 }}>Sent</div>
              <div style={{ ...styles.tableCell, flex: 1.5 }}>Expires</div>
            </div>
            {invites.map(invite => {
              const status = getInviteStatus(invite);
              return (
                <div key={invite.id} style={styles.tableRow}>
                  <div style={{ ...styles.tableCell, flex: 2, fontWeight: 500 }}>
                    {invite.email}
                  </div>
                  <div style={{ ...styles.tableCell, flex: 1 }}>
                    <span
                      style={{
                        ...styles.roleBadge,
                        backgroundColor: invite.role === 'admin' ? '#e3f2fd' : '#fff3e0',
                        color: invite.role === 'admin' ? '#1565c0' : '#e65100',
                      }}
                    >
                      {invite.role}
                    </span>
                  </div>
                  <div style={{ ...styles.tableCell, flex: 1.5 }}>
                    {invite.inviterEmail}
                  </div>
                  <div style={styles.tableCell}>
                    <span
                      style={{
                        ...styles.statusBadge,
                        backgroundColor: `${status.color}20`,
                        color: status.color,
                      }}
                    >
                      {status.text}
                    </span>
                  </div>
                  <div style={{ ...styles.tableCell, flex: 1.5 }}>
                    {formatDate(invite.createdAt)}
                  </div>
                  <div style={{ ...styles.tableCell, flex: 1.5 }}>
                    {formatDate(invite.expiresAt)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '2rem',
  },
  heading: {
    fontSize: '2rem',
    marginBottom: '0.5rem',
    color: '#333',
  },
  description: {
    fontSize: '1rem',
    color: '#666',
    margin: 0,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    marginBottom: '1.5rem',
  },
  cardTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#333',
    marginTop: 0,
    marginBottom: '1.5rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  formRow: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
  },
  inputGroup: {
    flex: 1,
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontSize: '14px',
    fontWeight: '600',
    color: '#555',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease',
  },
  inputError: {
    borderColor: '#dc3545',
  },
  error: {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '6px',
  },
  button: {
    padding: '10px 24px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '26px',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.2s ease',
  },
  buttonDisabled: {
    backgroundColor: '#90caf9',
    cursor: 'not-allowed',
  },
  loadingText: {
    textAlign: 'center',
    color: '#666',
    padding: '2rem',
  },
  emptyText: {
    textAlign: 'center',
    color: '#999',
    padding: '2rem',
  },
  table: {
    display: 'flex',
    flexDirection: 'column',
  },
  tableHeader: {
    display: 'flex',
    padding: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '4px',
    fontWeight: '600',
    fontSize: '14px',
    color: '#555',
    marginBottom: '8px',
  },
  tableRow: {
    display: 'flex',
    padding: '12px',
    borderBottom: '1px solid #eee',
    fontSize: '14px',
    color: '#333',
  },
  tableCell: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    padding: '0 8px',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
    backgroundColor: 'white',
    cursor: 'pointer',
  },
  roleBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'capitalize',
  },
};
